import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './../components/ui/button';
import { Input } from './../components/ui/input';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './../components/ui/hover-card';
import { toast } from 'sonner';
import { ContactDialog } from '../components/ContactDialog';
import { ContactHoverCard } from '../components/ContactHoverCard';
import { Search, Plus, Upload, Download, Loader2, Pencil, Trash2, UserRound, BadgeCheck, Briefcase, Home, Mail, MapPin, Trophy, Inbox, Reply, MailWarning } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TYPE_COLORS = {
  'Broker': 'border-sky-500/40 bg-sky-500/10 text-sky-300',
  'Owner': 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  'OLTA Investor': 'border-violet-500/40 bg-violet-500/10 text-violet-300',
  'Equity Gap Investor': 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  'Affiliate': 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300',
};
const typeClass = (t) => TYPE_COLORS[t] || 'border-border bg-secondary/40 text-muted-foreground';

const stageChip = (stage, label) => {
  const cls = stage === 'passed' ? 'border-red-600/40 bg-red-600/10 text-red-400'
    : ['loi_signed', 'dd', 'closing'].includes(stage) ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
      : 'border-border bg-secondary/50 text-foreground/70';
  return `text-[9px] font-mono border rounded px-1 py-0.5 shrink-0 ${cls}`;
};

// Hoverable "N deals" badge listing the linked deals with stages
function DealsBadge({ entry, testid }) {
  const navigate = useNavigate();
  if (!entry) return <span className="text-muted-foreground">—</span>;
  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        <span data-testid={testid}
          className="inline-flex items-center gap-1 text-xs font-mono text-sky-300 border border-sky-600/40 bg-sky-600/10 rounded px-1.5 py-0.5 cursor-pointer hover:bg-sky-600/20 transition-colors">
          <Inbox className="w-3 h-3" /> {entry.total}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="bg-card border-border rounded-sm w-72 p-2 z-[120]" data-testid="contact-deals-hover">
        <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1.5">
          Inbound Deals ({entry.total}) · {entry.active} active{entry.passed ? ` · ${entry.passed} passed` : ''}
        </p>
        <div className="space-y-1 max-h-56 overflow-y-auto">
          {entry.deals.map(d => (
            <button key={d.id} onClick={() => navigate(`/projects/${d.id}`)}
              className="w-full flex items-center justify-between gap-2 px-1.5 py-1 rounded-sm hover:bg-secondary/60 text-left transition-colors">
              <span className="text-xs text-foreground truncate">{d.name}</span>
              <span className={stageChip(d.stage, d.stage_label)}>{d.stage_label}</span>
            </button>
          ))}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

const RANK_COLORS = ['text-amber-400', 'text-zinc-300', 'text-orange-400'];

// Fit rate from the Broker Deal Inbox: created deals / processed proposals
export function FitBadge({ fit, testid }) {
  if (!fit) return null;
  const r = fit.fit_rate;
  const cls = r === null ? 'border-border bg-secondary/40 text-muted-foreground'
    : r >= 50 ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
      : r >= 25 ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
        : 'border-red-500/40 bg-red-500/10 text-red-400';
  return (
    <span data-testid={testid}
      title={`Broker Inbox Fit-Quote: ${fit.total} Deal${fit.total !== 1 ? 's' : ''} eingereicht · ${fit.created} angelegt · ${fit.declined + fit.dismissed} abgesagt/dismissed${fit.open ? ` · ${fit.open} offen` : ''}`}
      className={`inline-flex items-center gap-1 text-[9px] font-mono border rounded px-1.5 py-0.5 shrink-0 ${cls}`}>
      🎯 {r === null ? 'neu' : `${r}% Fit`}
    </span>
  );
}

function BrokerRankingCard({ ranking }) {
  const navigate = useNavigate();
  const brokers = ranking.filter(e => (e.type || '') !== 'Owner');
  if (!brokers.length) return null;
  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="contacts-ranking-card">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-amber-400" />
        <h3 className="font-heading text-base text-foreground">Broker Ranking</h3>
        <span className="text-[10px] text-muted-foreground">which brokers &amp; advisors bring us the most deals — owners excluded</span>
      </div>
      <div className="space-y-1.5">
        {brokers.slice(0, 8).map((e, i) => (
          <div key={e.id} className="flex items-center gap-3 px-2.5 py-2 bg-secondary/30 border border-border rounded-sm"
            data-testid={`ranking-row-${i}`}>
            <span className={`w-6 text-center font-mono font-bold text-sm shrink-0 ${RANK_COLORS[i] || 'text-muted-foreground'}`}>#{i + 1}</span>
            <span className="w-8 h-8 rounded-full overflow-hidden bg-secondary/60 border border-border shrink-0 flex items-center justify-center">
              {e.has_photo
                ? <img src={`${API_BASE}/api/contacts/${e.id}/photo/file`} alt="" className="w-full h-full object-cover" loading="lazy" />
                : <UserRound className="w-4 h-4 text-muted-foreground/40" />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-foreground truncate">
                  {[e.first_name, e.last_name].filter(Boolean).join(' ') || e.company}
                </span>
                {e.type && <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 shrink-0 ${typeClass(e.type)}`}>{e.type}</span>}
                <FitBadge fit={e.inbox_fit} testid={`ranking-fit-${i}`} />
                {e.has_photo && e.photo_verified && <BadgeCheck className="w-3 h-3 text-emerald-400 shrink-0" />}
              </div>
              <div className="flex items-center gap-1 flex-wrap mt-0.5">
                {e.deals.slice(0, 4).map(d => (
                  <button key={d.id} onClick={() => navigate(`/projects/${d.id}`)} title={d.stage_label}
                    className="text-[9px] font-mono text-muted-foreground border border-border rounded px-1 py-0.5 hover:text-foreground hover:border-foreground/40 transition-colors truncate max-w-[130px]">
                    {d.name}
                  </button>
                ))}
                {e.deals.length > 4 && <span className="text-[9px] text-muted-foreground">+{e.deals.length - 4} more</span>}
              </div>
            </div>
            {e.best_stage_label && (
              <span className={stageChip(e.best_stage, e.best_stage_label)} title="Furthest active deal">{e.best_stage_label}</span>
            )}
            <div className="text-right shrink-0">
              <p className="text-lg font-mono font-bold text-sky-300 leading-none" data-testid={`ranking-total-${i}`}>{e.total}</p>
              <p className="text-[9px] text-muted-foreground">
                inbound{e.passed ? ` · ${e.passed} passed` : ''}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ContactsPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [typeFilter, setTypeFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [ranking, setRanking] = useState([]);
  const [dialogDefaults, setDialogDefaults] = useState(null);
  const [apolloCredits, setApolloCredits] = useState(null);
  const importRef = useRef(null);

  // deep link: /contacts?new=1&deal=..&company=..&type=Owner  or  /contacts?edit={contactId}
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('new') === '1') {
      setDialogDefaults({
        projectId: params.get('deal') || '',
        company: params.get('company') || '',
        type: params.get('type') || '',
      });
      setEditing(null);
      setDialogOpen(true);
      window.history.replaceState({}, '', '/contacts');
    } else if (params.get('edit')) {
      api.getContact(params.get('edit'))
        .then(r => { setEditing(r.data); setDialogOpen(true); })
        .catch(() => toast.error('Contact not found'));
      window.history.replaceState({}, '', '/contacts');
    }
  }, []);

  const loadRanking = useCallback(() => {
    api.getContactsRanking()
      .then(r => setRanking(r.data.ranking || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.getApolloCredits()
      .then(r => { if (r.data.configured) setApolloCredits(r.data); })
      .catch(() => {});
  }, [dialogOpen]);

  const load = useCallback(() => {
    api.getContacts(search, typeFilter)
      .then(r => { setRows(r.data.contacts); setTotal(r.data.total); setTypes(r.data.types); })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to load contacts'))
      .finally(() => setLoading(false));
    loadRanking();
  }, [search, typeFilter, loadRanking]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const rankingById = React.useMemo(() => Object.fromEntries(ranking.map(e => [e.id, e])), [ranking]);

  const remove = async (c) => {
    if (!window.confirm(`Delete contact "${[c.first_name, c.last_name].filter(Boolean).join(' ') || c.company}"?`)) return;
    try { await api.deleteContact(c.id); toast.success('Contact deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const r = await api.importContacts(file);
      toast.success(`Import done — ${r.data.created} created, ${r.data.updated} updated${r.data.skipped ? `, ${r.data.skipped} skipped` : ''}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    setImporting(false);
    if (importRef.current) importRef.current.value = '';
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.getContactsTemplate();
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'olta_contacts_template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const exportContacts = async () => {
    try {
      const r = await api.exportContacts();
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'olta_contacts_export.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed (admin only)'); }
  };

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="contacts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground tracking-tight">Contacts</h1>
          <p className="text-muted-foreground text-sm mt-1">{total} contact{total !== 1 ? 's' : ''} — owners auto-populate from landing pages</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={importRef} type="file" accept=".xlsx" className="hidden"
            onChange={e => doImport(e.target.files?.[0])} data-testid="contacts-import-input" />
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="contacts-template-btn"
            className="border-border text-muted-foreground hover:text-foreground rounded-sm text-xs h-8 px-3">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Template
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={exportContacts} data-testid="contacts-export-btn"
              className="border-amber-600/40 text-amber-400 hover:bg-amber-600/10 rounded-sm text-xs h-8 px-3">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => importRef.current?.click()} disabled={importing}
            data-testid="contacts-import-btn"
            className="border-blue-600/40 text-blue-400 hover:bg-blue-600/10 rounded-sm text-xs h-8 px-3">
            {importing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />} Bulk Import
          </Button>
          <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="contacts-add-btn"
            className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-xs h-8 px-3">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Contact
          </Button>
        </div>
      </div>

      <BrokerRankingCard ranking={ranking} />

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, company, email…"
            data-testid="contacts-search-input"
            className="pl-8 bg-secondary border-border text-foreground rounded-sm h-8 text-sm w-64" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-testid="contacts-type-filter"
          className="text-sm bg-secondary border border-border text-foreground rounded-sm px-2 h-8 cursor-pointer focus:outline-none">
          <option value="">All types</option>
          {types.map(t => <option key={t.id} value={t.label}>{t.label}</option>)}
        </select>
        {apolloCredits && (
          <span data-testid="apollo-credits-badge"
            title={`Apollo credits remaining (account: ${apolloCredits.account_email}) — Email & LinkedIn pulls use email/export credits (1 per revealed email), phone pulls use phone credits (~8 per mobile number)`}
            className="ml-auto inline-flex items-center gap-1.5 text-[10px] font-mono text-orange-300 border border-orange-500/40 bg-orange-500/10 rounded px-2 py-1">
            Apollo credits: <strong>{apolloCredits.lead_remaining}</strong> Email/LinkedIn · <strong>{apolloCredits.phone_remaining}</strong> Phone
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm py-14 text-center">
          <UserRound className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No contacts yet — add one manually or bulk import brokers via Excel.</p>
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto bg-card">
          <table className="w-full text-sm min-w-[1000px]">
            <thead>
              <tr className="text-left text-[10px] text-muted-foreground uppercase tracking-wider border-b border-border">
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Company</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Deals</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Phone</th>
                <th className="px-3 py-2 font-medium">Private Address</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c, i) => (
                <tr key={c.id} className="border-b border-border/50 hover:bg-secondary/20 transition-colors" data-testid={`contact-row-${i}`}>
                  <td className="px-3 py-2">
                    <ContactHoverCard name={[c.first_name, c.last_name].filter(Boolean).join(' ')} email={c.email}>
                      <span className="flex items-center gap-2 cursor-pointer">
                        <span className="w-7 h-7 rounded-full overflow-hidden bg-secondary/60 border border-border shrink-0 flex items-center justify-center">
                          {c.has_photo
                            ? <img src={`${API_BASE}/api/contacts/${c.id}/photo/file`} alt="" className="w-full h-full object-cover" loading="lazy" />
                            : <UserRound className="w-3.5 h-3.5 text-muted-foreground/40" />}
                        </span>
                        <span className="text-foreground truncate max-w-[180px]">
                          {[c.title, c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                        </span>
                        {c.has_photo && c.photo_verified && <BadgeCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" title="Photo verified" />}
                      </span>
                    </ContactHoverCard>
                  </td>
                  <td className="px-3 py-2 text-foreground/80 truncate max-w-[180px]">{c.company || '—'}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      {c.type ? <span className={`text-[10px] font-mono border rounded px-1.5 py-0.5 ${typeClass(c.type)}`}>{c.type}</span> : '—'}
                      {c.campaign_response && (
                        <span data-testid={`contact-responded-${i}`}
                          title={`Replied ${new Date(c.campaign_response.at).toLocaleDateString('de-DE')}: „${c.campaign_response.subject}"${c.campaign_response.campaign_name ? ` — ${c.campaign_response.campaign_name}` : ''}`}
                          className="inline-flex items-center gap-1 text-[9px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded px-1.5 py-0.5">
                          <Reply className="w-3 h-3" /> Responded {new Date(c.campaign_response.at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                        </span>
                      )}
                      {c.email_bounced && (
                        <span data-testid={`contact-bounced-${i}`}
                          title={`Email bounced ${new Date(c.email_bounced.at).toLocaleDateString('de-DE')}${c.email_bounced.campaign_name ? ` — ${c.email_bounced.campaign_name}` : ''}. Update the email address to clear this flag.`}
                          className="inline-flex items-center gap-1 text-[9px] font-mono border border-orange-500/40 bg-orange-500/10 text-orange-300 rounded px-1.5 py-0.5">
                          <MailWarning className="w-3 h-3" /> Bounced
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      <DealsBadge entry={rankingById[c.id]} testid={`contact-deals-${i}`} />
                      <FitBadge fit={c.inbox_fit} testid={`contact-fit-${i}`} />
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {c.email ? (
                      <span className="flex items-center gap-1.5 flex-wrap">
                        <a href={`mailto:${c.email}`} className="text-blue-400 hover:underline flex items-center gap-1 text-xs"><Mail className="w-3 h-3" />{c.email}</a>
                        {c.apollo?.applied && c.apollo?.email === c.email && (
                          <span data-testid={`contact-apollo-badge-${i}`}
                            title={`Found via Apollo.io on ${c.apollo.checked_at ? new Date(c.apollo.checked_at).toLocaleDateString('de-DE') : ''} — status: ${c.apollo.email_status || 'unknown'}`}
                            className={`inline-flex items-center gap-0.5 text-[9px] font-mono border rounded px-1 py-0 ${c.apollo.email_status === 'verified' ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-amber-500/50 bg-amber-500/10 text-amber-300'}`}>
                            {c.apollo.email_status === 'verified' ? '✓ Verified' : `~ ${c.apollo.email_status || 'guessed'}`}
                          </span>
                        )}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-foreground/80">
                    {c.phone_work && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3 text-muted-foreground" />{c.phone_work}</span>}
                    {c.phone_private && (
                      <span className="flex items-center gap-1 mt-0.5">
                        <Home className="w-3 h-3 text-muted-foreground" />{c.phone_private}
                        {c.apollo_phone?.applied && (c.apollo_phone.numbers || []).some(n => n.number === c.phone_private) && (
                          <span data-testid={`contact-apollo-phone-badge-${i}`}
                            className="inline-flex items-center text-[9px] font-mono border border-orange-500/50 bg-orange-500/10 text-orange-300 rounded px-1 py-0">
                            via Apollo
                          </span>
                        )}
                      </span>
                    )}
                    {!c.phone_work && !c.phone_private && '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[200px]">
                    {c.private_address ? <span className="flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" />{c.private_address}</span> : '—'}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => { setEditing(c); setDialogOpen(true); }} data-testid={`contact-edit-${i}`}
                      className="text-muted-foreground hover:text-blue-400 p-1"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(c)} data-testid={`contact-delete-${i}`}
                      className="text-muted-foreground hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ContactDialog open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) setDialogDefaults(null); }}
        contact={editing} types={types} isAdmin={isAdmin} onSaved={load}
        projectId={dialogDefaults?.projectId || ''}
        defaultCompany={dialogDefaults?.company || ''}
        defaultType={dialogDefaults?.type || ''} />
    </div>
  );
}
